/**
 * PassiveLivenessStep
 * ===================
 * Browser-based passive liveness detection using anti-spoof model (ONNX Runtime).
 * No user actions required — just look at the camera.
 * Works on ALL devices (WASM backend, no WebGL needed).
 *
 * Flow: Look at camera → model analyzes ~10 frames → pass/fail
 * ~2 seconds total, no challenges.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Camera,
  CheckCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
  Shield,
} from 'lucide-react';
import { useAntiSpoof } from './useAntiSpoof';
import { useReportStepError } from '../../embed/EmbedContext';

const MAX_RETRIES = 5;
const REQUIRED_FRAMES = 10;
// Lowered to 0.03 for mobile cameras - model struggles in low light
// Will let most real users pass but may also let some spoofs through
const PASS_THRESHOLD = 0.03;
const ANALYSIS_INTERVAL_MS = 200; // 5 fps analysis

export default function PassiveLivenessStep({ onComplete, onBack, client }) {
  const [phase, setPhase] = useState('intro'); // intro, loading, scanning, success, failed
  const [retryCount, setRetryCount] = useState(0);
  const [cameraError, setCameraError] = useState(null);
  useReportStepError('liveness', cameraError);
  const [progress, setProgress] = useState(0); // 0-100

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const intervalRef = useRef(null);
  const scoresRef = useRef([]);
  const frameCountRef = useRef(0);

  const { isLoading: modelLoading, loadProgress, error: modelError, detect } = useAntiSpoof();

  // Start camera
  const startCamera = useCallback(async () => {
    try {
      setCameraError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      setCameraError(err.message || 'Camera access denied');
    }
  }, []);

  // Stop camera and analysis
  const stopCamera = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, []);

  // Capture selfie from current video frame
  const captureSelfie = useCallback(() => {
    return new Promise((resolve) => {
      const video = videoRef.current;
      if (!video || !video.videoWidth) {
        resolve(null);
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92);
    });
  }, []);

  // Start analysis loop
  const startAnalysis = useCallback(() => {
    if (!detect) return;
    scoresRef.current = [];
    frameCountRef.current = 0;
    setProgress(0);

    intervalRef.current = setInterval(async () => {
      const video = videoRef.current;
      if (!video || video.readyState < 2) return;

      const result = await detect(video);
      frameCountRef.current++;

      if (result) {
        scoresRef.current.push(result.score);
        const analyzed = scoresRef.current.length;
        setProgress(Math.min((analyzed / REQUIRED_FRAMES) * 100, 100));

        // Check if we have enough frames
        if (analyzed >= REQUIRED_FRAMES) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;

          // Calculate average score
          const avgScore = scoresRef.current.reduce((a, b) => a + b, 0) / scoresRef.current.length;
          const isReal = avgScore > PASS_THRESHOLD;

          if (isReal) {
            // Capture selfie before stopping camera
            const selfieBlob = await captureSelfie();
            setPhase('success');
            stopCamera();

            // Report to backend
            const confidence = Math.min(avgScore * 100, 99.0);
            if (client) {
              try {
                await client.completeLiveness('passive-local', confidence, {
                  provider: 'passive',
                  anti_spoof_score: avgScore,
                  frames_analyzed: analyzed,
                });
              } catch (e) {
                console.warn('Failed to report passive liveness:', e);
              }
            }
            setTimeout(() => {
              onComplete({
                is_live: true,
                confidence,
                provider: 'passive',
                selfieBlob,
                anti_spoof_score: avgScore,
                frames_analyzed: analyzed,
              });
            }, 1500);
          } else {
            setPhase('failed');
            stopCamera();
          }
        }
      } else {
        // Model returned null — face might not be centered
        // (user should position face in oval)
      }
    }, ANALYSIS_INTERVAL_MS);
  }, [detect, captureSelfie, stopCamera, client, onComplete]);

  // Handle phase transitions
  useEffect(() => {
    if (phase === 'loading' && !modelLoading && !modelError) {
      startCamera().then(() => {
        setPhase('scanning');
      });
    }
  }, [phase, modelLoading, modelError, startCamera]);

  // Attach stream to video element when scanning phase renders
  useEffect(() => {
    if (phase === 'scanning' && videoRef.current && streamRef.current) {
      if (!videoRef.current.srcObject) {
        videoRef.current.srcObject = streamRef.current;
        videoRef.current.play().catch(() => {});
      }
    }
  }, [phase]);

  // Start analysis when scanning phase begins
  useEffect(() => {
    if (phase === 'scanning' && videoRef.current) {
      // Wait a moment for camera to stabilize
      const timer = setTimeout(() => startAnalysis(), 500);
      return () => clearTimeout(timer);
    }
  }, [phase, startAnalysis]);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopCamera();
  }, [stopCamera]);

  // Handle start
  const handleStart = useCallback(() => {
    setPhase('loading');
  }, []);

  // Handle retry
  const handleRetry = useCallback(() => {
    if (retryCount >= MAX_RETRIES) return;
    setRetryCount(prev => prev + 1);
    scoresRef.current = [];
    frameCountRef.current = 0;
    setProgress(0);
    setPhase('loading');
  }, [retryCount]);

  // === INTRO PHASE ===
  if (phase === 'intro') {
    return (
      <div style={styles.container}>
        <div style={styles.introContent}>
          <div style={styles.iconCircle}>
            <Shield size={28} color="#0D9488" />
          </div>
          <h3 style={styles.title}>Liveness Check</h3>
          <p style={styles.subtitle}>
            We'll verify you're a real person. Just look at the camera naturally — no special actions needed.
          </p>
          <div style={styles.tipsList}>
            <div style={styles.tip}>
              <Camera size={16} color="#0D9488" />
              <span>Good lighting on your face</span>
            </div>
            <div style={styles.tip}>
              <Shield size={16} color="#0D9488" />
              <span>Remove sunglasses or hats</span>
            </div>
          </div>
          <p style={styles.tipNote}>
            This takes about 2 seconds. No head movements required.
          </p>
          <button style={styles.primaryButton} onClick={handleStart}>
            <Camera size={18} />
            Start Verification
          </button>
          {onBack && (
            <button style={styles.backButton} onClick={onBack}>
              Go Back
            </button>
          )}
        </div>
      </div>
    );
  }

  // === LOADING PHASE ===
  if (phase === 'loading') {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContent}>
          <Loader2 size={32} color="#0D9488" style={{ animation: 'spin 1s linear infinite' }} />
          <h3 style={styles.title}>Preparing...</h3>
          <p style={styles.subtitle}>
            {modelLoading ? 'Loading AI model...' : 'Starting camera...'}
          </p>
          {modelLoading && (
            <div style={styles.progressBar}>
              <div style={{ ...styles.progressFill, width: `${loadProgress}%` }} />
            </div>
          )}
          {modelError && (
            <div style={styles.errorBox}>
              <AlertCircle size={16} color="#EF4444" />
              <span>{modelError}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // === SUCCESS PHASE ===
  if (phase === 'success') {
    return (
      <div style={styles.container}>
        <div style={styles.successContent}>
          <div style={styles.successIcon}>
            <CheckCircle size={36} color="#10B981" />
          </div>
          <h3 style={styles.title}>Liveness Verified!</h3>
          <p style={styles.subtitle}>Your identity has been confirmed.</p>
        </div>
      </div>
    );
  }

  // === FAILED PHASE ===
  if (phase === 'failed') {
    return (
      <div style={styles.container}>
        <div style={styles.failedContent}>
          <div style={styles.failedIcon}>
            <AlertCircle size={32} color="#EF4444" />
          </div>
          <h3 style={styles.title}>Verification Failed</h3>
          <p style={styles.subtitle}>
            We couldn't confirm liveness. Please try again in good lighting.
          </p>
          {retryCount < MAX_RETRIES ? (
            <button style={styles.primaryButton} onClick={handleRetry}>
              <RefreshCw size={18} />
              Try Again ({MAX_RETRIES - retryCount} attempts left)
            </button>
          ) : (
            <p style={styles.errorText}>Maximum attempts reached. Please contact support.</p>
          )}
          {onBack && (
            <button style={styles.backButton} onClick={onBack}>
              Go Back
            </button>
          )}
        </div>
      </div>
    );
  }

  // === SCANNING PHASE ===
  return (
    <div style={styles.container}>
      {/* Camera feed */}
      <div style={styles.cameraContainer}>
        <video
          ref={videoRef}
          style={styles.video}
          playsInline
          muted
          autoPlay
        />
        {/* Face oval overlay */}
        <div style={styles.ovalOverlay}>
          <svg viewBox="0 0 300 400" style={styles.ovalSvg}>
            <defs>
              <mask id="passiveOvalMask">
                <rect width="300" height="400" fill="white" />
                <ellipse cx="150" cy="200" rx="110" ry="150" fill="black" />
              </mask>
            </defs>
            <rect width="300" height="400" fill="rgba(0,0,0,0.5)" mask="url(#passiveOvalMask)" />
            <ellipse
              cx="150" cy="200" rx="110" ry="150"
              fill="none"
              stroke={progress >= 100 ? '#10B981' : '#0D9488'}
              strokeWidth="3"
              strokeDasharray={`${progress * 7.5} 750`}
            />
          </svg>
        </div>

        {/* Camera error */}
        {cameraError && (
          <div style={styles.cameraErrorOverlay}>
            <AlertCircle size={24} color="#EF4444" />
            <p>{cameraError}</p>
          </div>
        )}
      </div>

      {/* Status text */}
      <div style={styles.statusBar}>
        <Shield size={20} color="#0D9488" />
        <p style={styles.statusText}>
          {progress < 100 ? 'Analyzing... Hold still' : 'Processing...'}
        </p>
      </div>

      {/* Progress bar */}
      <div style={styles.analysisProgress}>
        <div style={{ ...styles.analysisProgressFill, width: `${progress}%` }} />
      </div>

    </div>
  );
}

/* SDK Brand Colors - matches VerificationSDK.jsx */
const PRIMARY = '#0D9488';
const PRIMARY_LIGHT = '#CCFBF1';
const ACCENT = '#D97706';

/* iOS 2026 Standards: 22px title, 17px body, 44px touch targets */
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 'min(400px, 60vh)',
    padding: '16px',
    position: 'relative',
  },
  introContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '20px',
  },
  loadingContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: '12px',
  },
  successContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  failedContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  iconCircle: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    background: PRIMARY_LIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  successIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    background: '#D1FAE5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  failedIcon: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    background: '#FEE2E2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  title: {
    fontSize: '22px',
    fontWeight: '600',
    color: '#1F2937',
    margin: '0 0 8px',
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: '17px',
    color: '#6B7280',
    margin: '0 0 20px',
    lineHeight: '1.5',
  },
  tipsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '20px',
    width: '100%',
    maxWidth: '300px',
  },
  tip: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '17px',
    color: '#374151',
    textAlign: 'left',
  },
  tipNote: {
    fontSize: '15px',
    color: '#9CA3AF',
    margin: '0 0 20px',
    maxWidth: '300px',
  },
  primaryButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '16px 28px',
    background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`,
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '14px',
    fontSize: '17px',
    fontWeight: '600',
    cursor: 'pointer',
    marginBottom: '12px',
    minHeight: '50px', /* iOS primary button */
    minWidth: '200px',
  },
  backButton: {
    padding: '12px 20px',
    background: 'transparent',
    color: '#6B7280',
    minHeight: '44px', /* iOS touch target */
    border: 'none',
    fontSize: '17px',
    cursor: 'pointer',
  },
  progressBar: {
    width: '200px',
    height: '6px',
    background: '#E5E7EB',
    borderRadius: '3px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    background: PRIMARY,
    borderRadius: '3px',
    transition: 'width 0.3s ease',
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '14px 16px',
    background: '#FEF2F2',
    borderRadius: '12px',
    fontSize: '15px',
    color: '#DC2626',
    marginTop: '12px',
  },
  errorText: {
    fontSize: '15px',
    color: '#EF4444',
    margin: '8px 0',
  },
  cameraContainer: {
    position: 'relative',
    width: '100%',
    maxWidth: 'min(320px, 90vw)',
    maxHeight: '45vh',
    aspectRatio: '3/4',
    borderRadius: '16px',
    overflow: 'hidden',
    background: '#000',
    marginBottom: '16px',
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transform: 'scaleX(-1)',
  },
  ovalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
  },
  ovalSvg: {
    width: '100%',
    height: '100%',
  },
  cameraErrorOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.8)',
    color: '#FFFFFF',
    padding: '20px',
    textAlign: 'center',
    fontSize: '17px',
  },
  statusBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  statusText: {
    fontSize: '17px',
    fontWeight: '500',
    color: '#1F2937',
    margin: 0,
  },
  analysisProgress: {
    width: 'min(220px, 80vw)',
    height: '6px',
    background: '#E5E7EB',
    borderRadius: '3px',
    overflow: 'hidden',
    marginBottom: '8px',
  },
  analysisProgressFill: {
    height: '100%',
    background: PRIMARY,
    borderRadius: '3px',
    transition: 'width 0.2s ease',
  },
  debugText: {
    fontSize: '11px',
    color: '#9CA3AF',
    margin: 0,
    fontFamily: 'monospace',
  },
};
